import { Fragment, type ReactNode } from 'react'
import { clamp01, pct } from './geometry'
import './viz.css'

export interface StripPoint {
  label: string
  value: number
  tone?: 'gain' | 'neutral'
  labelled?: boolean
}

export interface StripLine {
  value: number
  label: string
}

interface StripPlotProps {
  points: StripPoint[]
  domain: [number, number]
  lines: StripLine[]
  /** A string, or a node when part of it carries a tone (a warning marker); nothing draws no headline. */
  headline?: ReactNode
  ariaLabel: string
}

// A label closer than this (percent of the axis) to an end is anchored to that
// end instead of centred on its dot, so it cannot run outside the plot. The
// percentages assume the narrowest plot, a span-2 module (~172px of track), and
// 12px mono text (7.2px a character).
// 10% = 17px, the half-width of a 4-character label ("AXTI", 29px); a
// 5-character label overruns by ~1px, well inside the card's 12px padding.
const DOT_LABEL_EDGE = 10
// 25% = 43px, the half-width of an 11-character line label ("lowest held",
// 79px); a longer one overruns by its excess, up to the 12px card padding.
const LINE_LABEL_EDGE = 25

// The label slots, in the order they are handed out along the axis: above-near,
// below-near, above-far, below-far. A far label sits one label height further
// from the dot than a near one and hangs from a stem, so up to four labelled
// points, however close together, each get a slot of their own and no two
// labels can overlap. A fifth point reuses slot 0 (and so on, i % 4).
const SLOTS = [
  { side: 'above', far: false },
  { side: 'below', far: false },
  { side: 'above', far: true },
  { side: 'below', far: true },
] as const

/**
 * Position (0–100) of `value` on the domain. A domain with no extent (equal,
 * inverted or non-finite bounds) has no axis, so everything sits in the middle.
 */
function place(value: number, [lo, hi]: [number, number]): number {
  return Number.isFinite(lo) && Number.isFinite(hi) && hi > lo ? clamp01((value - lo) / (hi - lo)) * 100 : 50
}

/** Modifier class that keeps a label of the given block inside the plot. */
function anchor(block: string, x: number, edge: number): string {
  if (x < edge) return ` ${block}--start`
  if (x > 100 - edge) return ` ${block}--end`
  return ''
}

/**
 * One-dimensional dot plot on a fixed domain, with optional dashed reference
 * lines. Only labelled points get text. Taken in ascending-x order (ties keep
 * array order), the i-th labelled point gets slot `i % 4` (see SLOTS), however
 * the array is ordered. A point or line whose value is not a finite number has
 * no position and is not drawn.
 */
export function StripPlot({ points, domain, lines, headline, ariaLabel }: StripPlotProps) {
  const placed = points
    .map((point, index) => ({ point, index, x: place(point.value, domain) }))
    .filter(({ point }) => Number.isFinite(point.value))

  const slots = new Map<number, (typeof SLOTS)[number]>()
  placed
    .filter(({ point }) => point.labelled)
    .sort((a, b) => a.x - b.x)
    .forEach(({ index }, rank) => slots.set(index, SLOTS[rank % SLOTS.length]))

  const marks = lines
    .filter((line) => Number.isFinite(line.value))
    .map((line) => ({ line, x: place(line.value, domain) }))
  const lined = marks.some(({ line }) => Boolean(line.label))

  return (
    <div className={`viz-strip${lined ? ' viz-strip--lined' : ''}`} role="img" aria-label={ariaLabel}>
      {headline ? <div className="viz-strip-headline">{headline}</div> : null}
      <div className="viz-strip-track">
        {placed.map(({ point, index, x }) => {
          const slot = slots.get(index)
          return (
            <Fragment key={`${point.label}-${index}`}>
              <span
                className={`viz-strip-dot${point.tone === 'gain' ? ' viz-strip-dot--gain' : ''}`}
                style={{ left: pct(x) }}
              />
              {slot?.far ? (
                <span className={`viz-strip-stem viz-strip-stem--${slot.side}`} style={{ left: pct(x) }} />
              ) : null}
              {slot ? (
                <span
                  className={`viz-strip-dot-label viz-strip-dot-label--${slot.side}${slot.far ? ' viz-strip-dot-label--far' : ''}${anchor('viz-strip-dot-label', x, DOT_LABEL_EDGE)}`}
                  style={{ left: pct(x) }}
                >
                  {point.label}
                </span>
              ) : null}
            </Fragment>
          )
        })}
        {marks.map(({ line, x }, i) => (
          <span className="viz-strip-line" key={`${line.label}-${i}`} style={{ left: pct(x) }}>
            {line.label ? (
              <span className={`viz-strip-line-label${anchor('viz-strip-line-label', x, LINE_LABEL_EDGE)}`}>
                {line.label}
              </span>
            ) : null}
          </span>
        ))}
      </div>
    </div>
  )
}
